/**
 * PromoteKit — affiliate marketing kit panel for `/app/affiliate`.
 *
 * Surfaces everything an affiliate needs to actually USE their link:
 *   - QR code + download buttons (PNG)
 *   - Branded social cards (square / story / LinkedIn) — server-rendered PNG
 *   - Swipe copy library: Twitter / LinkedIn / Instagram / Email
 *   - Pitch video scripts (60s + 30s)
 *   - "Why I switched" testimonial template (fill-in-the-blank)
 *   - Email signature HTML — preview + copy-as-HTML button
 *   - Performance by channel — see which UTM source is moving the needle
 *
 * Everything is one click away. The whole point: remove the "what do I say?"
 * objection that kills most affiliate programs.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Copy, Download, QrCode, Twitter, Linkedin, Mail, Video, Camera,
  FileText, BarChart3, Sparkles, ChevronRight, Megaphone,
} from "lucide-react";

const API_BASE =
  (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";

function authHeaders() {
  const tok = localStorage.getItem("af_token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

async function fetchBlob(path) {
  const r = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  if (!r.ok) throw new Error(`Download failed (${r.status})`);
  return r.blob();
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

const SECTIONS = [
  { key: "qr",          label: "QR Code",            icon: QrCode },
  { key: "cards",       label: "Social Cards",       icon: Camera },
  { key: "twitter",     label: "Twitter / X",        icon: Twitter },
  { key: "linkedin",    label: "LinkedIn",           icon: Linkedin },
  { key: "instagram",   label: "IG · TikTok",        icon: Megaphone },
  { key: "email",       label: "Email",              icon: Mail },
  { key: "video",       label: "Video Scripts",      icon: Video },
  { key: "testimonial", label: "Case Study",         icon: FileText },
  { key: "signature",   label: "Email Signature",    icon: Sparkles },
  { key: "performance", label: "By Channel",         icon: BarChart3 },
];


export default function PromoteKit() {
  const [kit, setKit] = useState(null);
  const [perf, setPerf] = useState(null);
  const [active, setActive] = useState("qr");
  const [qrSrc, setQrSrc] = useState(null);
  const [cardPreviews, setCardPreviews] = useState({});  // variant → objectURL

  useEffect(() => {
    api.get("/affiliate/promo-kit")
      .then((r) => setKit(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load promo kit"));
    api.get("/affiliate/performance-by-channel")
      .then((r) => setPerf(r.data))
      .catch(() => { /* non-blocking */ });
  }, []);

  // Fetch QR + card preview blobs once kit is loaded — `<img src>` can't
  // attach the Authorization header, so we hydrate via fetch + object URLs.
  useEffect(() => {
    if (!kit) return undefined;
    let cancelled = false;
    const urls = [];
    (async () => {
      try {
        const qrBlob = await fetchBlob("/api/affiliate/qr.png?size=600");
        if (cancelled) return;
        const u = URL.createObjectURL(qrBlob); urls.push(u); setQrSrc(u);
      } catch { /* ignore — QR section will show skeleton */ }
      const previews = {};
      for (const c of kit.cards) {
        try {
          const blob = await fetchBlob(c.download_url);
          if (cancelled) return;
          const u = URL.createObjectURL(blob); urls.push(u);
          previews[c.variant] = u;
        } catch { /* skip on failure */ }
      }
      if (!cancelled) setCardPreviews(previews);
    })();
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [kit]);

  if (!kit) {
    return (
      <div className="card" data-testid="promote-kit-loading">
        <div className="h-6 w-40 bg-slate-100 animate-pulse rounded mb-3" />
        <div className="h-32 bg-slate-100 animate-pulse rounded" />
      </div>
    );
  }

  return (
    <section className="card p-0 overflow-hidden" data-testid="promote-kit">
      <div className="px-5 pt-5 pb-3 border-b border-[var(--line)] bg-gradient-to-br from-emerald-50/60 via-white to-amber-50/30">
        <div className="label-eyebrow flex items-center gap-1.5">
          <Sparkles size={11} /> Promote Kit · ready-to-paste
        </div>
        <h2 className="font-display font-extrabold text-2xl tracking-tight mt-1">
          Everything you need to share iSpeedToClose.
        </h2>
        <p className="text-sm text-[var(--ink-2)] mt-1 max-w-2xl">
          Hand-written swipe copy, a brand-styled QR code, and downloadable
          social cards — all auto-personalised with your referral link.
          Pick a channel, copy, paste, profit.
        </p>
      </div>

      {/* Section nav */}
      <div className="flex overflow-x-auto border-b border-[var(--line)] bg-white sticky top-0 z-10">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const isActive = active === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setActive(s.key)}
              className={`px-4 py-3 text-xs font-bold whitespace-nowrap inline-flex items-center gap-1.5 border-b-2 transition-colors ${
                isActive
                  ? "border-emerald-500 text-emerald-700 bg-emerald-50/40"
                  : "border-transparent text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-slate-50"
              }`}
              data-testid={`promote-kit-tab-${s.key}`}
            >
              <Icon size={13} /> {s.label}
            </button>
          );
        })}
      </div>

      <div className="p-5">
        {active === "qr"          && <QRSection kit={kit} qrSrc={qrSrc} />}
        {active === "cards"       && <CardsSection kit={kit} previews={cardPreviews} />}
        {active === "twitter"     && <TwitterSection data={kit.swipe.twitter} />}
        {active === "linkedin"    && <LinkedInSection data={kit.swipe.linkedin} />}
        {active === "instagram"   && <InstagramSection data={kit.swipe.instagram} />}
        {active === "email"       && <EmailSection data={kit.swipe.email} />}
        {active === "video"       && <VideoSection data={kit.swipe.video} />}
        {active === "testimonial" && <TestimonialSection data={kit.swipe.testimonial} />}
        {active === "signature"   && <SignatureSection html={kit.signature_html} />}
        {active === "performance" && <PerformanceSection perf={perf} />}
      </div>
    </section>
  );
}

// ─── QR ──────────────────────────────────────────────────────────────────

function QRSection({ kit, qrSrc }) {
  const downloadQR = async (size) => {
    try {
      const blob = await fetchBlob(`/api/affiliate/qr.png?size=${size}`);
      downloadBlob(blob, `ispeedtoclose-qr-${kit.referral_code}-${size}px.png`);
      toast.success(`QR downloaded · ${size}px`);
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="promote-kit-qr">
      <div className="rounded-xl border border-[var(--line)] bg-white p-5 flex flex-col items-center">
        <div className="rounded-xl border-2 border-emerald-100 p-3 bg-white">
          {qrSrc ? (
            <img
              src={qrSrc}
              alt="Your referral QR code"
              width={260}
              height={260}
              data-testid="promote-kit-qr-image"
            />
          ) : (
            <div className="w-[260px] h-[260px] bg-slate-100 animate-pulse rounded" />
          )}
        </div>
        <div className="text-xs text-[var(--ink-3)] mt-3 font-mono break-all text-center max-w-xs">
          {kit.referral_url}
        </div>
      </div>
      <div className="space-y-3">
        <div className="text-sm text-[var(--ink-2)]">
          Drop your QR on business cards, postcards, flyers, signs, or print
          mailers. Anyone who scans it lands on the iSpeedToClose homepage
          with your referral attached — 60-day cookie window.
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[400, 800, 1600].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => downloadQR(s)}
              className="btn-outline text-xs justify-center"
              data-testid={`promote-kit-qr-download-${s}`}
            >
              <Download size={12} /> {s}px
            </button>
          ))}
        </div>
        <CopyButton
          text={kit.referral_url}
          label="Copy referral URL"
          testid="promote-kit-qr-copy-url"
        />
      </div>
    </div>
  );
}

// ─── Social Cards ────────────────────────────────────────────────────────

function CardsSection({ kit, previews }) {
  const downloadCard = async (variant) => {
    try {
      const blob = await fetchBlob(`/api/affiliate/social-card.png?variant=${variant}`);
      downloadBlob(blob, `ispeedtoclose-${variant}-${kit.referral_code}.png`);
      toast.success(`${variant} card downloaded`);
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="promote-kit-cards">
      {kit.cards.map((c) => {
        const preview = previews[c.variant];
        return (
          <div
            key={c.variant}
            className="rounded-xl border border-[var(--line)] bg-white p-3 flex flex-col"
            data-testid={`promote-kit-card-${c.variant}`}
          >
            <div className="aspect-square bg-slate-50 rounded-lg overflow-hidden border border-slate-100 flex items-center justify-center mb-3">
              {preview ? (
                <img
                  src={preview}
                  alt={c.label}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="w-full h-full bg-slate-100 animate-pulse" />
              )}
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
                {c.label}
              </div>
              <div className="text-sm font-semibold text-[var(--ink)] mt-0.5">
                {c.w} × {c.h} px
              </div>
            </div>
            <button
              type="button"
              onClick={() => downloadCard(c.variant)}
              className="btn-money text-xs justify-center mt-3"
              data-testid={`promote-kit-card-download-${c.variant}`}
            >
              <Download size={12} /> Download PNG
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ─── Twitter ─────────────────────────────────────────────────────────────

function TwitterSection({ data }) {
  return (
    <div className="space-y-5" data-testid="promote-kit-twitter">
      <SubHeader title="Quick hooks" hint="One-liner posts. Pick → tweet." />
      <div className="space-y-2">
        {data.hooks.map((h, i) => (
          <CopyBlock key={i} text={h} testid={`promote-kit-twitter-hook-${i}`} />
        ))}
      </div>
      <SubHeader title="Threads" hint="Multi-tweet stories — high reach." />
      <div className="space-y-3">
        {data.threads.map((t, i) => (
          <CopyBlock key={i} title={t.title} text={t.body} testid={`promote-kit-twitter-thread-${i}`} />
        ))}
      </div>
    </div>
  );
}

// ─── LinkedIn ────────────────────────────────────────────────────────────

function LinkedInSection({ data }) {
  return (
    <div className="space-y-3" data-testid="promote-kit-linkedin">
      {data.posts.map((p, i) => (
        <CopyBlock key={i} title={p.title} text={p.body} testid={`promote-kit-linkedin-${i}`} />
      ))}
    </div>
  );
}

// ─── Instagram / TikTok ─────────────────────────────────────────────────

function InstagramSection({ data }) {
  return (
    <div className="space-y-2" data-testid="promote-kit-instagram">
      <SubHeader title="Short-form captions" hint="For Reels, TikToks, Shorts." />
      {data.captions.map((c, i) => (
        <CopyBlock key={i} text={c} testid={`promote-kit-ig-caption-${i}`} />
      ))}
    </div>
  );
}

// ─── Email ──────────────────────────────────────────────────────────────

function EmailSection({ data }) {
  return (
    <div className="space-y-3" data-testid="promote-kit-email">
      {data.templates.map((t, i) => (
        <div
          key={i}
          className="rounded-xl border border-[var(--line)] bg-white p-4"
          data-testid={`promote-kit-email-${i}`}
        >
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)] mb-1">
            Subject
          </div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-sm font-semibold text-[var(--ink)]">{t.subject}</div>
            <button
              type="button"
              className="text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 font-semibold"
              onClick={() => { navigator.clipboard.writeText(t.subject); toast.success("Subject copied"); }}
              data-testid={`promote-kit-email-${i}-copy-subject`}
            >
              <Copy size={11} /> Copy
            </button>
          </div>
          <pre className="text-xs text-[var(--ink-2)] whitespace-pre-wrap font-sans leading-relaxed bg-[var(--bg)] rounded-lg p-3 border border-[var(--line)]">{t.body}</pre>
          <div className="flex justify-end mt-2">
            <button
              type="button"
              className="btn-outline text-xs"
              onClick={() => { navigator.clipboard.writeText(t.body); toast.success("Body copied"); }}
              data-testid={`promote-kit-email-${i}-copy-body`}
            >
              <Copy size={12} /> Copy body
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Video scripts ──────────────────────────────────────────────────────

function VideoSection({ data }) {
  return (
    <div className="space-y-3" data-testid="promote-kit-video">
      {data.scripts.map((s, i) => (
        <CopyBlock key={i} title={s.title} text={s.body} testid={`promote-kit-video-${i}`} />
      ))}
    </div>
  );
}

// ─── Testimonial ────────────────────────────────────────────────────────

function TestimonialSection({ data }) {
  return (
    <div data-testid="promote-kit-testimonial">
      <CopyBlock title={data.label} text={data.body} testid="promote-kit-testimonial-body" />
    </div>
  );
}

// ─── Email signature ───────────────────────────────────────────────────

function SignatureSection({ html }) {
  return (
    <div className="space-y-4" data-testid="promote-kit-signature">
      <SubHeader
        title="Drop this into Gmail / Outlook / Apple Mail"
        hint="Every email you send becomes a referral surface."
      />
      <div className="rounded-xl border border-[var(--line)] bg-white p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)] mb-2">
          Preview
        </div>
        <div dangerouslySetInnerHTML={{ __html: html }} data-testid="promote-kit-signature-preview" />
      </div>
      <div className="rounded-xl border border-[var(--line)] bg-[var(--bg)] p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
            HTML
          </div>
          <button
            type="button"
            className="btn-outline text-xs"
            onClick={() => { navigator.clipboard.writeText(html); toast.success("Signature HTML copied"); }}
            data-testid="promote-kit-signature-copy"
          >
            <Copy size={12} /> Copy HTML
          </button>
        </div>
        <pre className="text-[10px] text-[var(--ink-2)] whitespace-pre-wrap font-mono leading-snug max-h-40 overflow-auto">{html}</pre>
      </div>
      <div className="text-xs text-[var(--ink-3)] bg-amber-50 border border-amber-200 rounded-lg p-3">
        💡 In Gmail: Settings → See all settings → Signature → paste this HTML.
        Replace <code className="font-mono">[YOUR NAME]</code>, <code className="font-mono">[Phone]</code>,
        and <code className="font-mono">[Email]</code> with your details.
      </div>
    </div>
  );
}

// ─── Performance ────────────────────────────────────────────────────────

function PerformanceSection({ perf }) {
  if (!perf) {
    return <div className="text-sm text-[var(--ink-3)]">Loading performance data…</div>;
  }
  if (!perf.channels || perf.channels.length === 0) {
    return (
      <div
        className="rounded-xl border border-dashed border-[var(--line)] p-8 text-center"
        data-testid="promote-kit-performance-empty"
      >
        <BarChart3 className="mx-auto mb-2 text-[var(--ink-3)]" size={26} />
        <div className="font-semibold text-[var(--ink)]">No channel data yet</div>
        <div className="text-sm text-[var(--ink-3)] mt-1 max-w-md mx-auto">
          Once you create custom UTM links below and start getting clicks,
          we&apos;ll break them down by channel here so you can see what&apos;s
          actually working.
        </div>
      </div>
    );
  }
  const max = Math.max(...perf.channels.map((c) => c.clicks), 1);
  return (
    <div className="space-y-3" data-testid="promote-kit-performance">
      <div className="text-sm text-[var(--ink-2)]">
        Total clicks across all channels: <strong className="text-[var(--ink)]">{perf.total_clicks}</strong>
      </div>
      <div className="space-y-2">
        {perf.channels.map((c) => (
          <div
            key={c.channel}
            className="flex items-center gap-3"
            data-testid={`promote-kit-channel-${c.channel}`}
          >
            <div className="w-24 text-xs font-bold text-[var(--ink-2)] capitalize shrink-0">
              {c.channel}
            </div>
            <div className="flex-1 h-6 bg-[var(--bg)] rounded relative overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded transition-all"
                style={{ width: `${(c.clicks / max) * 100}%` }}
              />
            </div>
            <div className="w-20 text-right text-xs">
              <span className="font-bold text-[var(--ink)]">{c.clicks}</span>
              <span className="text-[var(--ink-3)]"> clicks</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Shared bits ────────────────────────────────────────────────────────

function SubHeader({ title, hint }) {
  return (
    <div className="flex items-baseline gap-2">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
        {title}
      </div>
      {hint && <div className="text-xs text-[var(--ink-3)]">· {hint}</div>}
    </div>
  );
}

function CopyButton({ text, label, testid }) {
  return (
    <button
      type="button"
      className="btn-outline text-xs w-full justify-center"
      onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied!"); }}
      data-testid={testid}
    >
      <Copy size={12} /> {label}
    </button>
  );
}

function CopyBlock({ title, text, testid }) {
  return (
    <div
      className="rounded-xl border border-[var(--line)] bg-white p-4"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        {title && (
          <div className="text-sm font-semibold text-[var(--ink)] flex items-center gap-1.5">
            <ChevronRight size={14} className="text-emerald-600" />
            {title}
          </div>
        )}
        <button
          type="button"
          className="text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 font-semibold ml-auto"
          onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied!"); }}
          data-testid={testid ? `${testid}-copy` : undefined}
        >
          <Copy size={12} /> Copy
        </button>
      </div>
      <pre className="text-xs text-[var(--ink-2)] whitespace-pre-wrap font-sans leading-relaxed">{text}</pre>
    </div>
  );
}
